/**
 * Upload record API functions.
 */

import apiClient from './client';
import type { PaginatedResponse } from '@/types';

export interface UploadedFileItem {
  file_name: string;
  file_size: number;
  file_type_name?: string;
  auto_retry_count?: number;
  manual_retry_count?: number;
  upload_started_at?: string | null;
  upload_finished_at?: string | null;
  avg_speed_mbps?: number | null;
  change_type?: 'added' | 'deleted' | 'existing' | 'failed';
}

export type UploadRecordSource = 'bsp' | 'api' | 'bvp_bot' | 'rpa';

export interface UploadRecord {
  id: number;
  project: number;
  project_name: string;
  deliverable_type: 'package' | 'external_release';
  deliverable_id: number;
  source: UploadRecordSource;
  deliverable_name: string;
  vehicle_model: string;
  file_count: number;
  total_size: number;
  upload_status: 'uploading' | 'done' | 'failed';
  /** 列表展示态：后端按「与项目空间那一列一致」算好的（第三方平台上传期间为 uploading），
   *  展示与轮询都用这个；`upload_status` 是这条记录自身的历史状态，别混。 */
  display_upload_status: 'uploading' | 'done' | 'failed';
  is_first_upload: boolean;
  upload_type: 'new' | 'update' | 'retry';
  created_by_name: string;
  started_at: string;
  finished_at: string | null;
  duration_seconds: number | null;
  speed_mbps: number | null;
  upload_mode: string;
  chunk_size: number | null;
  uploaded_files: UploadedFileItem[];
  linked_items: string[];
  description: string;
  created_at: string;
}

export interface UploadRecordCreate {
  project: number;
  deliverable_type: 'package' | 'external_release';
  deliverable_id: number;
  deliverable_name: string;
  vehicle_model?: string;
  file_count?: number;
  total_size?: number;
  upload_status: 'uploading' | 'done' | 'failed';
  started_at: string;
  finished_at?: string;
  uploaded_files?: UploadedFileItem[];
  description?: string;
  upload_type?: 'new' | 'update' | 'retry';
}

export interface UploadRecordQueryParams {
  project_id?: number;
  project_name?: string;
  exclude_project_name?: string;
  deliverable_type?: 'package' | 'external_release';
  deliverable_id?: number;
  source?: UploadRecordSource;
  upload_status?: string;
  vehicle_model?: string;
  created_by_name?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface UploadRecordFilterOptions {
  project_names: string[];
  vehicle_models: string[];
  created_by_names: string[];
}

export const getUploadRecordFilterOptions = async (): Promise<UploadRecordFilterOptions> => {
  const response = await apiClient.get<UploadRecordFilterOptions>('/upload-records/filter-options/');
  return response.data;
};

export const listUploadRecords = async (
  params?: UploadRecordQueryParams
): Promise<PaginatedResponse<UploadRecord>> => {
  const response = await apiClient.get<PaginatedResponse<UploadRecord>>('/upload-records/', { params });
  return response.data;
};

export const createUploadRecord = async (data: UploadRecordCreate): Promise<UploadRecord> => {
  const response = await apiClient.post<UploadRecord>('/upload-records/', data);
  return response.data;
};

export interface UploadRecordUpdate {
  upload_status?: 'uploading' | 'done' | 'failed';
  finished_at?: string;
  uploaded_files?: UploadedFileItem[];
  file_count?: number;
  total_size?: number;
}

export const updateUploadRecord = async (id: number, data: UploadRecordUpdate): Promise<UploadRecord> => {
  const response = await apiClient.patch<UploadRecord>(`/upload-records/${id}/`, data);
  return response.data;
};

export const deleteUploadRecord = async (id: number): Promise<void> => {
  await apiClient.delete(`/upload-records/${id}/`);
};

export interface PeriodStats {
  total: number;
  done: number;
  failed: number;
  uploading: number;
  success_rate: number;
  fail_rate: number;
}

export type UploadStatsGranularity = 'day' | 'week' | 'month';

export interface UploadStatsRange {
  date_from: string;
  date_to: string;
  granularity: UploadStatsGranularity;
  deliverable_type: 'package' | 'external_release' | null;
}

export interface SourceStatEntry extends PeriodStats {
  source: UploadRecordSource;
}

export interface ProjectStatEntry extends PeriodStats {
  project_id: number;
  project_name: string;
}

export interface TimelineEntry {
  period: string;
  total: number;
  done: number;
  failed: number;
  uploading: number;
}

export interface UploadStatsParams {
  deliverable_type?: 'package' | 'external_release';
  project_id?: number;
  source?: UploadRecordSource;
  date_from?: string;
  date_to?: string;
  granularity?: UploadStatsGranularity;
}

export interface UploadStatsResponse {
  range: UploadStatsRange;
  summary: PeriodStats;
  by_source: SourceStatEntry[];
  by_project: ProjectStatEntry[];
  timeline: TimelineEntry[];
}

export const getUploadStats = async (
  params?: UploadStatsParams,
): Promise<UploadStatsResponse> => {
  const response = await apiClient.get<UploadStatsResponse>('/upload-records/stats/', { params });
  return response.data;
};
